import { useEffect, useRef } from 'react';
import { authService } from '../services/authService';

/**
 * Tab must have been away at least this long before a re-validation call is
 * worth making. Keeps quick alt-tabs / minimizes from firing a network
 * request on every return.
 */
const HIDDEN_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * User Story 112: catches a session that expired while the browser tab was
 * backgrounded, instead of waiting for the user's next click to surface it.
 *
 * Two independent signals mark the page "away", because neither alone covers
 * every way a user leaves the page:
 *  - `visibilitychange` fires when switching browser tabs or minimizing —
 *    but most browsers do NOT fire it when the user Alt-Tabs to a different
 *    application while this tab stays the active tab in an on-screen window.
 *  - `window.blur`/`focus` fire whenever the OS moves focus away from and
 *    back to the browser window, which is exactly the case the above misses.
 * Whichever fires first records the "away since" timestamp; whichever fires
 * on return checks the elapsed time and fires at most one re-validation.
 *
 * Once away for at least HIDDEN_THRESHOLD_MS, on return it fires one silent
 * GET /auth/profile call. That request already carries the JWT and is
 * already guarded on the backend, so:
 *  - valid session  -> the call just succeeds and nothing happens; the page
 *    resumes exactly as it was.
 *  - expired/invalid session -> the 401 is handled by apiClient's existing
 *    response interceptor (frontend/src/services/apiClient.ts), which clears
 *    the token and redirects to /login with no toast. This hook must NOT
 *    duplicate that logic, only trigger the check.
 *  - any other failure (e.g. a network blip right as the tab wakes) is
 *    swallowed here so it can never show an error or log the user out for a
 *    non-session reason.
 */
export function useSessionResume(): void {
  const awaySinceRef = useRef<number | null>(null);

  useEffect(() => {
    const markAway = () => {
      // Keep the earliest "away" timestamp — blur and visibilitychange can
      // both fire for the same departure (e.g. minimizing the window).
      if (awaySinceRef.current === null) {
        awaySinceRef.current = Date.now();
      }
    };

    const markBack = () => {
      // A stray blur/focus pair (e.g. clicking into devtools) can fire while
      // the tab itself is still visible — only proceed once it genuinely is.
      if (document.hidden) return;

      const awaySince = awaySinceRef.current;
      awaySinceRef.current = null;
      if (awaySince === null) return;
      if (Date.now() - awaySince < HIDDEN_THRESHOLD_MS) return;
      if (!localStorage.getItem('token')) return;

      authService.getProfile().catch(() => {
        // Swallowed intentionally — see doc comment above.
      });
    };

    const onVisibilityChange = () => (document.hidden ? markAway() : markBack());

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', markAway);
    window.addEventListener('focus', markBack);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', markAway);
      window.removeEventListener('focus', markBack);
    };
  }, []);
}
